import { z } from 'zod';
import { Bulletin, BulletinStatus, User, UserRole } from '@alucio/aws-beacon-amplify/src/models';

export const BULLETIN_HISTORY_DAYS = 60;
export const BULLETIN_TITLE_MAX_LENGTH = 150;
export const BULLETIN_CONTENT_MAX_LENGTH = 2000;
export const EMPTY_BULLETIN_CONTENT = '{"ops":[{"insert":"\\n"}]}';

const safeLink = z
  .string()
  .max(2048)
  .refine((value) => {
    try {
      return ['https:', 'http:', 'mailto:'].includes(new URL(value).protocol);
    } catch {
      return false;
    }
  }, 'Use an http, https or mailto link.');

const deltaSchema = z.object({
  ops: z
    .array(
      z.object({
        insert: z.string(),
        attributes: z
          .object({
            bold: z.boolean().optional(),
            italic: z.boolean().optional(),
            underline: z.boolean().optional(),
            header: z
              .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6)])
              .optional(),
            list: z.enum(['bullet', 'ordered']).optional(),
            indent: z.number().int().min(0).max(8).optional(),
            align: z.enum(['center', 'right', 'justify']).optional(),
            link: safeLink.optional(),
            font: z
              .enum([
                'Carlito',
                'Lato',
                'Oswald',
                'Poppins',
                'PT-Serif',
                'Arial',
                'Courier-New',
                'Times-New-Roman',
              ])
              .optional(),
            size: z.enum(['12px', '16px', '20px', '24px', '28px']).optional(),
            color: z
              .string()
              .regex(/^#[0-9a-f]{3,8}$/i)
              .optional(),
          })
          .optional(),
      }),
    )
    .max(20000),
});

export interface BulletinInput {
  title: string;
  content: string;
}

export function parseBulletinContent(content: unknown): string | null {
  if (typeof content === 'string' && content.length > 50000) return null;
  try {
    const result = deltaSchema.safeParse(
      typeof content === 'string' ? JSON.parse(content) : content,
    );
    if (!result.success) return null;
    const normalized = JSON.stringify(result.data);
    return normalized.length <= 50000 ? normalized : null;
  } catch {
    return null;
  }
}

export function validateBulletinInput(input: BulletinInput, requireContent = true): BulletinInput {
  const title = input.title.trim();
  if (!title || title.length > BULLETIN_TITLE_MAX_LENGTH) {
    throw new Error(`Enter a title of up to ${BULLETIN_TITLE_MAX_LENGTH} characters.`);
  }
  const content = parseBulletinContent(input.content);
  if (!content) throw new Error('Check the content and links before publishing.');
  const delta = deltaSchema.parse(JSON.parse(content));
  // Quill includes a mandatory final newline in the stored content.
  const text = delta.ops.map((op) => op.insert).join('').replace(/\n$/, '');
  if (text.length > BULLETIN_CONTENT_MAX_LENGTH) {
    throw new Error(`Enter content of up to ${BULLETIN_CONTENT_MAX_LENGTH} characters.`);
  }
  if (requireContent && !text.trim()) throw new Error('Add content before publishing.');
  return { title, content };
}

export function canManageBulletins(user?: Pick<User, 'role' | 'additionalRoles'>): boolean {
  return (
    user?.role === UserRole.TENANT_VIEWER &&
    !!user.additionalRoles?.some((role) => role === UserRole.LEAD_VIEWER)
  );
}

export type PublishedBulletin = Bulletin & { publishedAt: string };

export function getRecentBulletins(
  records: readonly Bulletin[],
  tenantId?: string,
  now = new Date(),
): PublishedBulletin[] {
  const cutoff = now.getTime() - BULLETIN_HISTORY_DAYS * 24 * 60 * 60 * 1000;
  return records
    .filter((record): record is PublishedBulletin => {
      const published = Date.parse(record.publishedAt || '');
      return (
        !!tenantId &&
        record.tenantId === tenantId &&
        record.status === BulletinStatus.ACTIVE &&
        published <= now.getTime() &&
        published >= cutoff
      );
    })
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt) || b.id.localeCompare(a.id));
}

export function getUnreadBulletin(
  records: readonly Bulletin[],
  viewedIds: readonly string[] | undefined,
  loginAt: Date,
): PublishedBulletin | undefined {
  return [...records]
    .filter(
      (record): record is PublishedBulletin =>
        record.status === BulletinStatus.ACTIVE &&
        Date.parse(record.publishedAt || '') <= loginAt.getTime() &&
        !viewedIds?.includes(record.id),
    )
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))[0];
}
